import ByteBuffer from 'bytebuffer';
import {
  CandidateNotification,
  CandidateRequest,
  CandidateResponse,
  DataChunk,
  NodeMessage,
  NodeMessageTypes,
  NodeState,
  PeerCandidate,
  PeerClusterDetach,
  DataStart,
  PeerStateUpdate,
  ClosePeer,
  BaseContentMessage,
  KeepAlive,
  BulkObjectNotification,
  ObjectNotification,
  BaseObjectNotification
} from './NodeMessages';
import { Attachment, NodeData, PeeringProtocol, TransportType } from './data';

export const MESSAGE_TYPES = Object.freeze({
  NODE_MESSAGE: 1,
  DATA_START: 2,
  DATA: 3,
  SHUTDOWN: 6,
  KEEP_ALIVE: 7
});

export function makeMessage(
  type: number,
  data?: ArrayBuffer | null
): ArrayBuffer {
  if (data) {
    return new ByteBuffer(data.byteLength + 1)
      .writeByte(type)
      .append(data)
      .flip().buffer;
  }
  return new ByteBuffer().writeByte(type).flip().buffer;
}

/*
 * Encoding/decoding for message types sent between peers. These map one-to-one with the message protocol defined
 * in the peering kernel package, and in the peering protodef files.
 *
 * NOTE: The decoders for top level message types MUST return an array of their decoded members.
 * The array will be disassembled into separate parameters before being passed to the kernel.
 *
 * Top level encoders are those that return a byte buffer. Top level decoders do not accept a byte buffer as an
 * argument.
 *
 * Some decoders also use the kernel module itself to recreate the cpp vectors that originally formed the message.
 */

/*
 * Helper decoders. Used to encode/decode the components of messages
 */

export class TextEncoderDecoder {
  static decode(bytes: ArrayBuffer): string {
    const bb = ByteBuffer.wrap(bytes);
    return bb.readCString();
  }

  static encode(text: string): ArrayBuffer {
    return new ByteBuffer().writeCString(text).flip().buffer;
  }
}

class NodeStateEncoderDecoder {
  static encode(bb: ByteBuffer, node: NodeState) {
    bb.writeInt32(node.upCapacity);
    bb.writeInt32(node.midCapacity);
    bb.writeInt32(node.downCapacity);
  }

  static decode(bb: ByteBuffer): NodeState {
    const upCapacity = bb.readInt32();
    const midCapacity = bb.readInt32();
    const downCapacity = bb.readInt32();
    return { upCapacity, midCapacity, downCapacity };
  }
}

class StringArrayEncoderDecoder {
  static encode(bb: ByteBuffer, strArr: string[]) {
    bb.writeInt32(strArr.length);
    for (let i = 0; i < strArr.length; i++) {
      bb.writeCString(strArr[i]);
    }
  }

  static decode(bb: ByteBuffer): string[] {
    const strings = [];
    const numStrings = bb.readInt32();
    for (let i = 0; i < numStrings; i++) {
      strings.push(bb.readCString());
    }
    return strings;
  }
}

/**
 * Peer Message Encoders/Decoders
 */
/**
 * Content Messages - Encoded/decoded as part of a Peer Message
 */

class BaseContentMessageEncoderDecoder {
  static decode(bb: ByteBuffer): BaseContentMessage {
    const from = bb.readCString();
    const to = bb.readCString();
    const objectId = bb.readCString();
    const metadata = bb.readCString();
    return { objectId, from, to, metadata };
  }

  static encode(bb: ByteBuffer, msg: BaseContentMessage) {
    bb.writeCString(msg.from);
    bb.writeCString(msg.to);
    bb.writeCString(msg.objectId);
    bb.writeCString(msg.metadata);
  }
}

class BaseObjectNotificationEncoderDecoder {
  static decode(bb: ByteBuffer): BaseObjectNotification {
    const type = bb.readInt32();
    const objectId = bb.readCString();
    return { type, objectId };
  }

  static encode(bb: ByteBuffer, notification: BaseObjectNotification) {
    bb.writeInt32(notification.type);
    bb.writeCString(notification.objectId);
  }
}

class BulkObjectNotificationEncoderDecoder {
  static decode(bb: ByteBuffer): BulkObjectNotification {
    const from = bb.readCString();
    const to = bb.readCString();
    const len = bb.readUint32();
    const objects = [];
    for (let i = 0; i < len; i++) {
      objects.push(BaseObjectNotificationEncoderDecoder.decode(bb));
    }
    return { from, to, objects };
  }

  static encode(bb: ByteBuffer, notification: BulkObjectNotification) {
    bb.writeCString(notification.from);
    bb.writeCString(notification.to);
    bb.writeUint32(notification.objects.length);
    for (let i = 0; i < notification.objects.length; i++) {
      BaseObjectNotificationEncoderDecoder.encode(bb, notification.objects[i]);
    }
  }
}

class ObjectNotificationEncoderDecoder {
  static decode(bb: ByteBuffer): ObjectNotification {
    const from = bb.readCString();
    const to = bb.readCString();
    return { from, to, ...BaseObjectNotificationEncoderDecoder.decode(bb) };
  }

  static encode(bb: ByteBuffer, notification: ObjectNotification) {
    bb.writeCString(notification.from);
    bb.writeCString(notification.to);
    BaseObjectNotificationEncoderDecoder.encode(bb, notification);
  }
}

/**
 * Candidate Message Encoder/Decoders
 */
class CandidateRequestEncoderDecoder {
  static decode(bb: ByteBuffer): CandidateRequest {
    const from = bb.readCString();
    const to = bb.readCString();
    const path = StringArrayEncoderDecoder.decode(bb);
    return { from, to, path };
  }

  static encode(bb: ByteBuffer, request: CandidateRequest) {
    bb.writeCString(request.from);
    bb.writeCString(request.to);
    StringArrayEncoderDecoder.encode(bb, request.path);
  }
}

class ProtocolEncoderDecoder {
  static encode(bb: ByteBuffer, protocol: PeeringProtocol) {
    bb.writeInt32(Number.parseInt(protocol.majorVersion));
    bb.writeInt32(Number.parseInt(protocol.minorVersion));
    StringArrayEncoderDecoder.encode(bb, protocol.uris);
  }

  static decode(bb: ByteBuffer): PeeringProtocol {
    const majorVersion = `${bb.readInt32()}`;
    const minorVersion = `${bb.readInt32()}`;
    const uris = StringArrayEncoderDecoder.decode(bb);
    return { majorVersion, minorVersion, type: TransportType.WEBRTC, uris };
  }
}

class PeerCandidateEncoderDecoder {
  static encode(bb: ByteBuffer, candidate: PeerCandidate) {
    bb.writeCString(candidate.nodeId);
    bb.writeCString(candidate.agentId);
    bb.writeCString(candidate.contentId);
    bb.writeCString(candidate.tenantId);
    bb.writeInt32(candidate.depth);
    StringArrayEncoderDecoder.encode(bb, candidate.upstreams);
    NodeStateEncoderDecoder.encode(bb, candidate.state);
    ProtocolEncoderDecoder.encode(bb, candidate.protocol);
  }

  static decode(bb: ByteBuffer): PeerCandidate {
    const nodeId = bb.readCString();
    const agentId = bb.readCString();
    const contentId = bb.readCString();
    const tenantId = bb.readCString();
    const depth = bb.readInt32();
    const upstreams = StringArrayEncoderDecoder.decode(bb);
    const state = NodeStateEncoderDecoder.decode(bb);
    const protocol = ProtocolEncoderDecoder.decode(bb);
    return {
      nodeId,
      agentId,
      contentId,
      tenantId,
      depth,
      upstreams,
      state,
      protocol
    };
  }
}

class CandidateArrayEncoderDecoder {
  static encode(bb: ByteBuffer, candidates: PeerCandidate[]) {
    bb.writeInt32(candidates.length);
    candidates.forEach(element =>
      PeerCandidateEncoderDecoder.encode(bb, element)
    );
  }

  static decode(bb: ByteBuffer): PeerCandidate[] {
    const length = bb.readInt32();
    const candidates = [];
    for (let i = 0; i < length; i++) {
      candidates.push(PeerCandidateEncoderDecoder.decode(bb));
    }
    return candidates;
  }
}

export class CandidateResponseEncoderDecoder {
  static decode(bb: ByteBuffer): CandidateResponse {
    const from = bb.readCString();
    const to = bb.readCString();
    const status = bb.readInt32();
    const candidates = CandidateArrayEncoderDecoder.decode(bb);
    const path = StringArrayEncoderDecoder.decode(bb);
    return { from, to, status, candidates, path };
  }

  static encode(bb: ByteBuffer, response: CandidateResponse) {
    bb.writeCString(response.from);
    bb.writeCString(response.to);
    bb.writeInt32(response.status);
    // SRV-4343 - enforce that candidates are defined as an empty array at least.
    CandidateArrayEncoderDecoder.encode(bb, response.candidates || []);
    // SRV-4343 - enforce that path is defined as an empty array at least.
    StringArrayEncoderDecoder.encode(bb, response.path || []);
  }
}

class CandidateNotificationEncoderDecoder {
  static decode(bb: ByteBuffer): CandidateNotification {
    const from = bb.readCString();
    const to = bb.readCString();
    const candidate = PeerCandidateEncoderDecoder.decode(bb);
    return { from, to, candidate };
  }

  static encode(bb: ByteBuffer, notification: CandidateNotification) {
    bb.writeCString(notification.from);
    bb.writeCString(notification.to);
    PeerCandidateEncoderDecoder.encode(bb, notification.candidate);
  }
}

class PeerStateUpdateEncoderDecoder {
  static decode(bb: ByteBuffer): PeerStateUpdate {
    const from = bb.readCString();
    const to = bb.readCString();
    const node = NodeInfoEncoderDecoder.decode(bb);
    return { from, to, node };
  }
  static encode(bb: ByteBuffer, update: PeerStateUpdate) {
    bb.writeCString(update.from);
    bb.writeCString(update.to);
    NodeInfoEncoderDecoder.encode(bb, update.node);
  }
}

class AttachmentEncoderDecoder {
  static decode(bb: ByteBuffer): Attachment {
    const id = bb.readCString();
    const protocolMajorVersion = bb.readInt32();
    const upstreams = StringArrayEncoderDecoder.decode(bb);
    const depth = bb.readInt32();
    return {
      activeCluster: {
        id,
        transportType: TransportType.WEBRTC,
        protocolMajorVersion,
        maxDepth: 0
      },
      upstreams,
      depth
    };
  }
  static encode(bb: ByteBuffer, attachment: Attachment) {
    bb.writeCString(attachment.activeCluster.id);
    bb.writeInt32(attachment.activeCluster.protocolMajorVersion);
    StringArrayEncoderDecoder.encode(bb, attachment.upstreams);
    bb.writeInt32(attachment.depth);
  }
}

class NodeInfoEncoderDecoder {
  static decode(bb: ByteBuffer): NodeData {
    const id = bb.readCString();
    const agentId = bb.readCString();
    const tenantId = bb.readCString();
    const contentId = bb.readCString();
    const hasAttachment = bb.readInt32();
    const attachment = hasAttachment
      ? AttachmentEncoderDecoder.decode(bb)
      : undefined;
    const protocol = ProtocolEncoderDecoder.decode(bb);
    const state = NodeStateEncoderDecoder.decode(bb);
    const locality = bb.readCString();
    return {
      id,
      agentId,
      tenantId,
      contentId,
      attachment,
      protocol,
      state,
      locality
    };
  }
  static encode(bb: ByteBuffer, node: NodeData) {
    bb.writeCString(node.id);
    bb.writeCString(node.agentId);
    bb.writeCString(node.tenantId);
    bb.writeCString(node.contentId);
    if (node.attachment) {
      bb.writeInt32(1); //has attachment
      AttachmentEncoderDecoder.encode(bb, node.attachment);
    } else {
      bb.writeInt32(0); //no attachment
    }
    ProtocolEncoderDecoder.encode(bb, node.protocol);
    NodeStateEncoderDecoder.encode(bb, node.state);
    bb.writeCString(node.locality);
  }
}

class UpdateEncoderDecoder {
  static decode(bb: ByteBuffer) {
    return { node: NodeInfoEncoderDecoder.decode(bb) };
  }

  static encode(bb: ByteBuffer, node: NodeData) {
    NodeInfoEncoderDecoder.encode(bb, node);
  }
}

class ClusterDetachEncoderDecoder {
  static decode(bb: ByteBuffer) {
    const peerId = bb.readCString();
    const sourceId = bb.readCString();
    const detachingPeer = NodeInfoEncoderDecoder.decode(bb);
    return { peerId, sourceId, detachingPeer };
  }

  static encode(bb: ByteBuffer, detach: PeerClusterDetach) {
    bb.writeCString(detach.peerId);
    bb.writeCString(detach.sourceId);
    NodeInfoEncoderDecoder.encode(bb, detach.detachingPeer);
  }
}

export class ClosePeerEncoderDecoder {
  static decode(bb: ByteBuffer): ClosePeer {
    const from = bb.readCString();
    const to = bb.readCString();
    const reason = bb.readInt32();
    return { from, to, reason };
  }
  static encode(bb: ByteBuffer, msg: ClosePeer): void {
    bb.writeCString(msg.from);
    bb.writeCString(msg.to);
    bb.writeInt32(msg.reason);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NodeMessageEncoderMap = new Map<string, any>([
  [NodeMessageTypes.PEER_OBJECT_REQUEST, BaseContentMessageEncoderDecoder],
  [NodeMessageTypes.OBJECT_NOTIFICATION, ObjectNotificationEncoderDecoder],
  [
    NodeMessageTypes.BULK_OBJECT_NOTIFICATION,
    BulkObjectNotificationEncoderDecoder
  ],
  [NodeMessageTypes.GRAPH_OBJECT_REQUEST, BaseContentMessageEncoderDecoder],
  [NodeMessageTypes.CANDIDATE_REQUEST, CandidateRequestEncoderDecoder],
  [NodeMessageTypes.CANDIDATE_RESPONSE, CandidateResponseEncoderDecoder],
  [
    NodeMessageTypes.CANDIDATE_NOTIFICATION,
    CandidateNotificationEncoderDecoder
  ],
  [NodeMessageTypes.PEER_STATE_UPDATE, PeerStateUpdateEncoderDecoder],
  [NodeMessageTypes.NODE_UPDATE, UpdateEncoderDecoder],
  [NodeMessageTypes.PEER_DETACH, ClusterDetachEncoderDecoder],
  [NodeMessageTypes.CLOSE_PEER, ClosePeerEncoderDecoder]
]);

export class NodeMessageEncoderDecoder {
  static decode(bytes: ArrayBuffer): NodeMessage {
    const bb = ByteBuffer.wrap(bytes);
    const type: string = bb.readCString();
    const body = NodeMessageEncoderMap.get(type).decode(bb);
    return <NodeMessage>{ type, body };
  }
  static encode(msg: NodeMessage): ArrayBuffer {
    const bb = new ByteBuffer();
    bb.writeCString(msg.type);
    NodeMessageEncoderMap.get(msg.type).encode(bb, msg.body);
    return bb.flip().compact().buffer;
  }
}

// Content response messages are encoded/decoded as a separate connection message
// since they require special handling
export class DataStartEncoderDecoder {
  static decode(bytes: ArrayBuffer): DataStart {
    const bb = ByteBuffer.wrap(bytes);
    const from = bb.readCString();
    const to = bb.readCString();
    const objectId = bb.readCString();
    const metadata = bb.readCString();
    const status = bb.readInt32();
    const numChunks = bb.readInt32();
    let firstChunk = undefined;
    if (numChunks > 0) {
      firstChunk = bb.toArrayBuffer();
    }
    return { from, to, objectId, metadata, status, firstChunk, numChunks };
  }

  static encode(response: DataStart): ArrayBuffer {
    const bb = new ByteBuffer();
    bb.writeCString(response.from);
    bb.writeCString(response.to);
    bb.writeCString(response.objectId);
    bb.writeCString(response.metadata);
    bb.writeInt32(response.status);
    let numChunks = 0;
    if (response.numChunks) {
      numChunks = response.numChunks;
    }
    bb.writeInt32(numChunks);
    if (numChunks > 0 && response.firstChunk) {
      bb.append(response.firstChunk);
    }
    return bb.flip().compact().buffer;
  }
}

export class DataEncoderDecoder {
  static decode(bytes: ArrayBuffer): DataChunk {
    const bb = ByteBuffer.wrap(bytes);
    const objectId = bb.readCString();
    const index = bb.readInt32();
    const data = bb.toArrayBuffer();
    return { objectId, index, data };
  }

  static encode(chunk: DataChunk): ArrayBuffer {
    return new ByteBuffer()
      .writeCString(chunk.objectId)
      .writeInt32(chunk.index)
      .append(chunk.data)
      .flip()
      .compact().buffer;
  }
}

export type Message = DataStart | NodeMessage | KeepAlive | DataChunk;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EncoderMap = new Map<number, any>([
  [MESSAGE_TYPES.NODE_MESSAGE, NodeMessageEncoderDecoder],
  [MESSAGE_TYPES.DATA, DataEncoderDecoder],
  [MESSAGE_TYPES.DATA_START, DataStartEncoderDecoder]
]);
